/**
 * Wirtschaftssystem von DUELINE (Masterprompt Abschnitte 63, 64, 70, 75, 78).
 *
 * Alle Werte in dieser Datei sind VERBINDLICH und stammen direkt aus dem
 * Masterprompt. Sie duerfen nicht ohne ausdrueckliche Produktentscheidung
 * geaendert werden.
 */

/** Gold, mit dem jeder Spieler in das Match startet. */
export const STARTING_GOLD = 250;

/**
 * Income, den jeder Spieler zu Matchbeginn besitzt.
 *
 * Von 50 auf 60 (v0.45), dann auf 250 (v0.51) - beides Produktentscheidungen
 * vom 29.09.2026.
 *
 * Der Sprung auf 250 gehoert zusammen mit dem gesenkten Zuschlag je Creep
 * (siehe incomeGain in creeps.ts): "hoch starten, flach wachsen". Gemeint ist
 * NICHT weniger Gold, sondern gleichmaessiger verteiltes Gold. Frueh ist mehr
 * da als vorher - eine Welle ist ab Minute zwei gross -, spaet weniger.
 *
 * Grund steht in BALANCE.md unter "Die Wirtschaftskurve": Das Income wuchs um
 * rund 40 Prozent je Minute, verdoppelte sich also alle zwei Minuten, und ueber
 * die Haelfte des gesamten Goldes kam in den letzten zwei Minuten herein.
 */
/*
 * 250 -> 220 (Produktentscheidung vom 05.10.2026).
 *
 * Zusammen mit den hoeheren Tierkosten der zweite Griff gegen ein
 * Earlygame, das vorbei war, bevor es anfing. Bewusst klein gehalten: Das
 * Einkommen traegt die ganze Wirtschaft, ein grosser Schnitt wuerde auch
 * Wellen und Defender verschieben. Das Startgold bleibt bei 250.
 *
 * 220 -> 200 (Produktentscheidung vom 10.10.2026).
 *
 * Der kleinste der drei Griffe dieses Tages und bewusst so: Der Startwert
 * verliert mit der Zeit an Gewicht. In Minute 11 stammen von 724 Income
 * nur noch 220 aus ihm, der Rest aus verschickten Creeps - ein Schnitt
 * hier trifft also vor allem die ersten Minuten.
 */
export const STARTING_INCOME = 200;

/**
 * Abstand zwischen zwei Income-Ausschuettungen in Sekunden.
 *
 * Von 15 auf 12 (Produktentscheidung vom 29.09.2026): ein Viertel mehr
 * Ausschuettungen ueber ein Match.
 *
 * Und am 10.10.2026 zurueck auf 15 (Produktentscheidung).
 *
 * Der haerteste der drei Griffe dieses Tages, obwohl er am harmlosesten
 * aussieht: Vier statt fuenf Ausschuettungen je Minute sind IMMER zwanzig
 * Prozent weniger Gold - in Minute 1 wie in Minute 20. Der Startwert
 * dagegen verliert mit der Zeit an Gewicht.
 */
export const INCOME_TICK_SECONDS = 15;

/**
 * Anteil des Kaufpreises, den ein Verteidiger beim Toeten eines Creeps erhaelt.
 * 10 Prozent, aber mindestens KILL_GOLD_MINIMUM.
 */
export const KILL_GOLD_RATIO = 0.1;
export const KILL_GOLD_MINIMUM = 2;

/*
 * Es gibt bewusst KEIN Goldlimit und KEIN Incomelimit.
 *
 * Als Kommentar statt als zwei Konstanten (01.10.2026): Nirgends im Code wird
 * Gold oder Income gedeckelt, und nichts fragte die beiden Werte ab. Sie sahen
 * aus wie Regler und waren keine.
 *
 * Die Felder im Netzwerkschema sind uint32 und tragen ueber vier Milliarden -
 * ein Limit ist also auch technisch nicht noetig.
 */

/**
 * Technologiestufen und ihre Freischaltkosten in Gold.
 *
 * Tier 3 von 2.000 auf 1.500 und Tier 4 von 6.000 auf 3.000
 * (Produktentscheidung vom 29.09.2026). Verbindlicher Wert aus dem
 * Masterprompt - geaendert nur wegen dieser ausdruecklichen Entscheidung.
 *
 * Grund: Die alten Preise stammen aus der steilen Wirtschaft, in der das
 * Income bis auf 1.832 wuchs. Seit v0.51 endet es bei 788, und 6.000 Gold
 * waren damit unerreichbar - der mittlere Endstand fiel auf Tier 3,02. Die
 * sieben Tier-4-Creeps und die 55-Punkte-Grenze waren totes Inventar.
 *
 * Die Zielvorstellung: Ein Aufstieg soll etwa zwei bis vier
 * Income-Auszahlungen Sparen kosten, sobald das Match ein Stueck gelaufen ist -
 * spuerbar, aber nicht das halbe Match.
 */
/*
 * Tierkosten +50 Prozent (Produktentscheidung vom 05.10.2026).
 *
 * Gemessen wurde Tier 2 nach 1,8 Minuten (fruehestens 41 Sekunden) und
 * Tier 3 nach 5,6 (fruehestens 3,4). Das Earlygame war damit vorbei,
 * bevor es anfing - man sprang durch die Stufen, statt sie zu erleben.
 *
 * An den Kosten gedreht und nicht am Einkommen, weil es nur den AUFSTIEG
 * bremsen soll: Wellen, Defender und Items bleiben gleich teuer.
 */
/*
 * Tier 4 von 4.500 auf 4.000 (Produktentscheidung vom 05.10.2026).
 *
 * Das Ziel: Die Haelfte der Spieler soll Tier 4 etwa um Minute 10 bis 12
 * erreichen. Gemessen mit zwei Startwerten (je 288 Spieler-Eintraege) waren
 * es 11 bzw. 12 Prozent, im Mittel erst bei 14:00 - bis Minute 12 null.
 *
 * Warum der Preis allein das Ziel nicht erreicht, ist vorgerechnet und soll
 * hier stehen bleiben, damit niemand spaeter mehr davon erwartet: Tier 3
 * faellt bei 8:36, und zwischen 8:36 und Minute 11 verdient ein Spieler
 * insgesamt nur rund 1.100 Gold. Wer in diesem Fenster jede Muenze sparen
 * wuerde - keine Welle, kein Defender - kaeme auf ein Viertel des Preises.
 *
 * 4.000 ist deshalb ein Schritt in die Richtung, kein Treffer.
 */
export const TIER_UPGRADE_COST: Readonly<Record<2 | 3 | 4, number>> = {
  2: 900,
  3: 2250,
  4: 4000,
};

/**
 * Maximale Army Points einer einzelnen Welle, abhaengig vom erreichten Tier.
 * Es gibt keine Zeit- oder Levelvoraussetzung: Tier Rush ist erlaubt.
 */
/*
 * Produktentscheidung vom 24.09.2026: Die Caps wurden VERDOPPELT.
 *
 * Grund aus dem Spieltest: Eine Welle zusammenzustellen war aufwaendig, und
 * was am Ende ankam, war fuer den Aufwand zu wenig. Jetzt schickt man seltener,
 * dafuer groesser - dazu gehoeren die Abklingzeit (WAVE_SEND_COOLDOWN_SECONDS)
 * und die Obergrenze an Creeps in einer Lane (MAX_SENT_CREEPS_PER_LANE).
 */
export const ARMY_POINT_CAP: Readonly<Record<1 | 2 | 3 | 4, number>> = {
  1: 15,
  2: 25,
  3: 40,
  4: 55,
};

/**
 * Wartezeit zwischen zwei Wellen, in Sekunden.
 *
 * Produktentscheidung vom 24.09.2026. Ohne Wartezeit koennte man die groesseren
 * Wellen einfach hintereinander wegschicken - der Gegner kaeme nie zum Atmen.
 *
 * Von 20 auf 30 erhoeht (Produktentscheidung vom 30.09.2026): "seltener, dafuer
 * wuchtiger". Gemessen an 864 Matches je Einstellung:
 *
 *   Abklingzeit   Wellen je Match   Creeps je Welle   Dauer
 *   20 s          33,4              3,5               11,8 Min
 *   25 s          28,2              3,6               12,2 Min
 *   30 s          25,5              3,7               12,9 Min
 *
 * Die Abklingzeit ALLEIN macht Wellen nicht groesser - sie macht sie nur
 * seltener, 20 Prozent weniger Creeps im ganzen Match. Damit aus "seltener"
 * auch "wuchtiger" wird, gehoeren zwei weitere Aenderungen desselben Tages
 * dazu: halbierte Creeppreise (creeps.ts) und ein Portal, das den staerkeren
 * Druck aushaelt (PORTAL_MAX_HP in match.ts).
 *
 * Sie verschiebt ausserdem die Heldenbalance deutlich: Der Deadeye gewann 15
 * Punkte Siegquote, der Arcanist verlor 13 - ohne dass ein Heldenwert
 * angefasst wurde. Weniger Gedraenge nutzt dem Einzelziel-Schuetzen und
 * schadet dem Flaechenzauberer. Heldenbalance wird deshalb erst NACH diesem
 * Regler eingestellt, nicht davor.
 */
export const WAVE_SEND_COOLDOWN_SECONDS = 30;

/**
 * Wie viele GESENDETE Creeps hoechstens gleichzeitig auf eine Lane zulaufen.
 *
 * Gezaehlt werden echte Einheiten, nicht Army Points (Produktentscheidung vom
 * 24.09.2026). Sie schuetzt die Uebersicht: Mehr als das kann niemand mehr
 * ueberblicken, und auf dem Handy wird es unleserlich.
 *
 * Nicht zu verwechseln mit MAX_ACTIVE_UNITS_PER_LANE: Das ist die harte
 * technische Obergrenze fuer ALLE Einheiten einer Lane - auch fuer
 * Beschworene und Splitterstuecke, die niemand gesendet hat.
 */
export const MAX_SENT_CREEPS_PER_LANE = 30;

/** Anzahl Item-Slots des Heroes. */
export const HERO_ITEM_SLOTS = 4;

/** Anteil des Kaufpreises, den der Verkauf eines Items zurueckgibt. */
export const ITEM_SELL_RATIO = 0.65;

/** Aufpreise der drei Item-Stufen (Masterprompt Abschnitt 78). */
/**
 * Goldkosten der vier Itemstufen (Produktentscheidung vom 27.09.2026).
 *
 * Die vierte Stufe ist bewusst teuer: Ab etwa Minute 8 kommt so viel Income
 * herein, dass ein volles Inventar in gut einer Minute bezahlt waere. Damit
 * war Kaufen keine Entscheidung mehr, sondern ein Knopf, den man irgendwann
 * drueckt. 5.000 Gold sind auch spaet noch mehrere Wellen wert.
 */
export const ITEM_TIER_COST = {
  t1: 250,
  t2: 550,
  t3: 1000,
  t4: 5000,
} as const;

/** Kosten des Revive Totems (Masterprompt Abschnitt 80). */
export const REVIVE_TOTEM_COST = 2000;
